import { type NewsSummary } from "@/features/mypage/api";
import {
  Box,
  ButtonBase,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { getCategorySx } from "@/features/mypage/utils/categoryStyles";

interface NewsProps extends NewsSummary {
  onClick: () => void;
  /** compact: バッジ＋タイトル(省略)＋日付を1行で詰めて表示（ピックアップ内のピン留め用）。 */
  variant?: "default" | "compact";
}

export const News: React.FC<NewsProps> = (props) => {
  const formattedDate = format(new Date(props.publishedAt), "yyyy/MM/dd", {
    locale: ja,
  });

  if (props.variant === "compact") {
    return (
      <ButtonBase
        component="div"
        onClick={props.onClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          minHeight: 36,
          px: 1,
          py: 0.75,
          borderRadius: "6px",
          justifyContent: "flex-start",
          "&:hover": { backgroundColor: "rgba(31, 41, 51, 0.035)" },
        }}
      >
        <Box
          sx={{
            ...getCategorySx(props.category),
            fontSize: 10.5,
            lineHeight: 1.4,
            minWidth: 56,
            textAlign: "center",
            px: 0.75,
            py: 0.1,
          }}
        >
          {props.category}
        </Box>
        <Typography
          sx={{
            minWidth: 0,
            flex: 1,
            fontSize: 13.5,
            fontWeight: 500,
            textAlign: "left",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {props.title}
        </Typography>
        <Typography sx={{ flexShrink: 0, fontSize: 12, color: "#7B8490" }}>
          {formattedDate}
        </Typography>
      </ButtonBase>
    );
  }

  const newsRecordSx: SxProps<Theme> = {
    padding: "0.5em",
    width: "100%",
    justifyContent: "flex-start",
    borderRadius: "4px",
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  };
  const dateTypographySx: SxProps<Theme> = {
    fontWeight: 500,
    fontSize: "14px",
    color: "darkgray.main",
    lineHeight: "1.4",
    padding: { xs: "0", md: "6px 0 0" },
  };
  const newsSummaryTextSx: SxProps<Theme> = {
    color: "black.main",
    fontWeight: 500,
    alignContent: "center",
    padding: { xs: "0", md: "4px 0" },
  };

  return (
    <>
      <ButtonBase component="div" sx={newsRecordSx} onClick={props.onClick}>
        {/* SP: 縦並び, MD以上: 横並び */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "4px", md: 2 },
            flexWrap: { xs: "wrap", md: "nowrap" },
            alignItems: "flex-start",
          }}
        >
          {/* SP: カテゴリと日付を横並び */}
          <Box
            sx={{
              display: { xs: "flex", md: "contents" },
              gap: 1,
              alignItems: "center",
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Box sx={getCategorySx(props.category)}>{props.category}</Box>
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <Typography sx={dateTypographySx}>{formattedDate}</Typography>
            </Box>
          </Box>
          {/* タイトル */}
          <Box sx={{ ...newsSummaryTextSx }}>
            <Typography
              sx={{
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: 500,
              }}
            >
              {props.title}
            </Typography>
          </Box>
          {/* MD以上: 日付を右端に */}
          <Box sx={{ display: { xs: "none", md: "block" }, flexShrink: 0 }}>
            <Typography sx={dateTypographySx}>{formattedDate}</Typography>
          </Box>
        </Box>
      </ButtonBase>
    </>
  );
};
